<template>
    <!-- the standings of one or more leagues, as one CSV file (the same figures as the website) -->
    <v-dialog v-model="show" max-width="560">
        <template v-slot:activator="{ on, attrs }">
            <v-btn depressed small outlined color="black" class="mr-2" v-bind="attrs" v-on="on">
                <v-icon left small>mdi-file-delimited-outline</v-icon>Export standings
            </v-btn>
        </template>
        <v-card>
            <v-card-title class="text-subtitle-1 font-weight-bold">
                <v-icon small color="primary" class="mr-2">mdi-file-delimited-outline</v-icon>Export standings (CSV)
            </v-card-title>
            <v-card-text class="pt-2">
                <v-select
                    v-model="seasonId"
                    :items="seasons"
                    :item-text="seasonLabel"
                    item-value="id"
                    label="Season"
                    outlined
                    dense
                />
                <v-select
                    v-model="phase"
                    :items="phases"
                    label="Phase"
                    outlined
                    dense
                />
                <v-autocomplete
                    v-model="leagueIds"
                    :items="leagueItems"
                    item-text="label"
                    item-value="id"
                    label="Leagues"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    outlined
                    dense
                    hide-details
                />
                <div class="d-flex mt-2">
                    <v-btn x-small text @click="leagueIds = leagueItems.map(item => item.id)">Select all</v-btn>
                    <v-btn x-small text @click="leagueIds = []">Clear</v-btn>
                </div>
                <div v-if="error" class="red--text text-caption mt-2">{{ error }}</div>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn text @click="show = false">Cancel</v-btn>
                <v-btn depressed color="black" class="white--text" :loading="loading" :disabled="!seasonId || !leagueIds.length" @click="exportCsv">
                    <v-icon left small>mdi-download</v-icon>Download CSV
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    import leagueService from '@/services/league'
    import seasonService from '@/services/season'
    import configService from '@/services/config'
    import { seasonLabel, sortSeasons } from '@/utils/season'

    const PHASES = [
        { text: 'Regular Season', value: 'regular_season' },
        { text: 'Playoffs', value: 'playoffs' },
        { text: 'Placements', value: 'placements' }
    ]

    // a CSV cell: quoted when it holds a comma, a quote or a new line
    function cell(value) {
        const text = value === null || value === undefined ? '' : String(value)
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
    }

    export default {
        name: 'ExportStandingsDialog',
        data() {
            return {
                show: false,
                loading: false,
                error: null,
                seasons: [],
                leagues: [],
                seasonId: null,
                phase: 'regular_season',
                phases: PHASES,
                leagueIds: []
            }
        },
        computed: {
            // the leagues by group, "Group · League"
            leagueItems() {
                return [...this.leagues]
                    .map(league => ({ id: league.id, label: league.leagueGroup ? `${league.leagueGroup.title} · ${league.title}` : league.title, active: league.active }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            }
        },
        watch: {
            show(open) {
                if (open && !this.seasons.length) {
                    this.load()
                }
            }
        },
        methods: {
            seasonLabel,
            load() {
                Promise.all([
                    seasonService.getSeasons(1, 100).then(resp => sortSeasons(resp.data.content || [])),
                    leagueService.getAllLeagues().then(resp => resp.data || []),
                    configService.getConfig().then(resp => resp.data).catch(() => ({}))
                ]).then(([seasons, leagues, config]) => {
                    this.seasons = seasons
                    this.leagues = leagues
                    // the active season of the Rules, and the active leagues
                    this.seasonId = (config && config.activeSeasonId) || (seasons[0] && seasons[0].id) || null
                    this.leagueIds = leagues.filter(league => league.active !== false).map(league => league.id)
                }).catch(() => {
                    this.error = 'Could not load the seasons and the leagues.'
                })
            },
            exportCsv() {
                this.loading = true
                this.error = null
                const selected = this.leagueItems.filter(item => this.leagueIds.includes(item.id))
                Promise.all(selected.map(league =>
                    leagueService.getRanking(league.id, this.seasonId, this.phase)
                        .then(resp => ({ league, rows: resp.data || [] }))
                        .catch(() => ({ league, rows: [], failed: true }))
                )).then(results => {
                    const header = ['League', 'Position', 'Team', 'GP', 'W', 'L', 'Win %', 'PTS+', 'PTS-', '+/-', 'Points']
                    const lines = [header.map(cell).join(',')]
                    results.forEach(({ league, rows }) => {
                        rows.forEach((row, index) => {
                            const games = (row.won || 0) + (row.lost || 0)
                            lines.push([
                                league.label,
                                row.position || index + 1,
                                row.name,
                                games,
                                row.won,
                                row.lost,
                                games ? ((row.won / games) * 100).toFixed(1) : '0.0',
                                row.pointsPlus,
                                row.pointsMinus,
                                row.pointsDiff,
                                row.standing
                            ].map(cell).join(','))
                        })
                    })
                    const failed = results.filter(result => result.failed).map(result => result.league.label)
                    if (lines.length === 1) {
                        this.error = 'There are no standings for this season and phase.'
                        return
                    }
                    this.download(lines.join('\r\n'))
                    if (failed.length) {
                        this.error = `Downloaded, without: ${failed.join(', ')}`
                    } else {
                        this.show = false
                    }
                }).finally(() => {
                    this.loading = false
                })
            },
            download(csv) {
                const season = this.seasons.find(item => item.id === this.seasonId)
                const name = `standings-${season ? seasonLabel(season).replace(/[^\w-]+/g, '-') : this.seasonId}-${this.phase}.csv`
                // the BOM lets Excel read the Greek names
                const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', name)
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
            }
        }
    }
</script>
